import { createContext, useMemo, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '@/api/core/ApiError';
import { AccessService } from '@/api/services/AccessService';
import { useAuthContext } from '@/contexts/AuthContext';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { ALL_MODULES, WEB_MODULES, isModule, type Module } from '@/lib/modules';

export const accessKeys = {
  all: ['access-me'] as const,
  // Keyed per user too: the /me portal's sign-out clears the query cache
  // (useMeSignOut, #1768), but the admin UserButton sign-out still does not, so
  // a second user on a shared laptop must not reuse the previous user's access.
  me: (orgId: string, userId: string) => [...accessKeys.all, orgId, userId] as const,
};

export interface AccessContextValue {
  isAdmin: boolean;
  authRole: 'admin' | 'employee';
  /** The professional role on the covering work contract; empty for an admin. */
  role: string;
  modules: ReadonlySet<Module>;
  /** The granted web modules in sidebar order. */
  webModules: readonly Module[];
  /** True when the caller holds at least one web module. */
  webAccess: boolean;
  hasModule: (module: Module) => boolean;
  isLoading: boolean;
  isError: boolean;
  /** The HTTP status of a failed access request, `null` otherwise. */
  errorStatus: number | null;
  /** True while a (re)fetch of the access is in flight. */
  isFetching: boolean;
  refetch: () => void;
}

export const AccessContext = createContext<AccessContextValue | undefined>(undefined);

const ALL_MODULE_SET: ReadonlySet<Module> = new Set(ALL_MODULES);
const EMPTY_MODULE_SET: ReadonlySet<Module> = new Set();

function errorStatusOf(error: unknown): number | null {
  return error instanceof ApiError ? error.status : null;
}

/**
 * AccessProvider resolves which modules the signed-in session holds (issue #1670).
 *
 * An admin holds every module and issues NO request. An employee issues one
 * `GET /api/v1/access/me`. The result drives the root gate, the route guards,
 * the sidebar and the hook-level gates. All of that is UX: the backend module
 * gates are the security boundary. A failed background refetch keeps the cached
 * access, except a 403, which revokes it.
 */
export function AccessProvider({ children }: { children: ReactNode }) {
  const isAdmin = useIsAdmin();
  const { isAuthReady, isSignedIn, orgId, userId } = useAuthContext();

  const query = useQuery({
    queryKey: accessKeys.me(orgId ?? '', userId ?? ''),
    queryFn: () => AccessService.getApiV1AccessMe(),
    enabled: !isAdmin && isSignedIn && isAuthReady && !!orgId,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    retry: (failureCount, error) => {
      const status = errorStatusOf(error);
      if (status === 401 || status === 403) return false;
      return failureCount < 2;
    },
  });

  const { data, status, error, isFetching, refetch } = query;

  const value = useMemo<AccessContextValue>(() => {
    const retry = () => {
      void refetch();
    };

    if (isAdmin) {
      return {
        isAdmin: true,
        authRole: 'admin',
        role: '',
        modules: ALL_MODULE_SET,
        webModules: WEB_MODULES,
        webAccess: true,
        hasModule: () => true,
        isLoading: false,
        isError: false,
        errorStatus: null,
        isFetching: false,
        refetch: retry,
      };
    }

    const failedStatus = status === 'error' ? errorStatusOf(error) : null;
    // A 403 on refetch means the role lost every module; any other refetch failure keeps the cached access.
    const revoked = failedStatus === 403;
    const access = revoked ? undefined : data;

    // Unknown keys from a newer backend are dropped.
    const modules: ReadonlySet<Module> =
      access !== undefined ? new Set(access.modules.filter(isModule)) : EMPTY_MODULE_SET;
    const webModules = WEB_MODULES.filter((module) => modules.has(module));
    const failed = status === 'error' && access === undefined;

    return {
      isAdmin: false,
      authRole: 'employee',
      role: access?.role ?? '',
      modules,
      webModules,
      webAccess: access !== undefined && access.webAccess && webModules.length > 0,
      hasModule: (module: Module) => modules.has(module),
      // `pending` also covers the disabled state before auth is ready.
      isLoading: status === 'pending',
      isError: failed,
      errorStatus: failed ? failedStatus : null,
      isFetching,
      refetch: retry,
    };
  }, [data, status, error, isFetching, isAdmin, refetch]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
}
